import Link from 'next/link';

import { useTranslation } from '@lichtblick/i18next';
import { Directions, FolderOpen, Mail, Phone } from '@lichtblick/icons';
import { Colors, TextVariants } from '@lichtblick/theme';
import { ContentVariants, SidesheetItem } from '@lichtblick/types';

import {
  CommonSection,
  Container,
  CustomerSection,
  IconWrapper,
  ProjectManagerWrapper,
  TextContainer,
} from './SidesheetSection.styles';

import { Chip } from '../../Chip';
import { Text } from '../../Text';

export type SidesheetSectionProps = {
  isShowBtn?: boolean;
  item: SidesheetItem;
};

const variantMap: Record<ContentVariants, { icon: React.ReactElement | null; prefix: string }> = {
  customer: {
    icon: null,
    prefix: '',
  },
  product: {
    icon: null,
    prefix: '',
  },
  address: {
    icon: <Directions />,
    prefix: 'https://maps.google.com/?q=',
  },
  phone: {
    icon: <Phone />,
    prefix: 'tel:',
  },
  email: {
    icon: <Mail />,
    prefix: 'mailto:',
  },
  documents: {
    icon: <FolderOpen />,
    prefix: '',
  },
  projectManager: {
    icon: <Mail />,
    prefix: 'mailto:',
  },
  eaasId: {
    icon: null,
    prefix: '',
  },
};

export const SidesheetSection = ({ isShowBtn = true, item: { items, type } }: SidesheetSectionProps) => {
  const { icon, prefix } = variantMap[type];
  const { t } = useTranslation();

  return (
    <Container data-testid="sidesheet-section">
      <Text $variant={TextVariants.title.m} color={Colors.blue[100]}>
        {t(`common:sidebar.${type}`)}
      </Text>
      {items?.map((item) => (
        <div key={`sidesheetitem-${type}-${item.value}-${item.subtitle}`}>
          {type === 'customer' && (
            <>
              <CustomerSection>
                <Text $variant={TextVariants.body.l} color={Colors.black}>
                  {item.label}
                </Text>
                <Chip shouldShowIcon={false} size="small" text={item.value} variant="descriptive-lightblue" />
              </CustomerSection>
              {item.contacts &&
                Object.values(item.contacts).map(
                  (contact) =>
                    contact && (
                      <CommonSection key={contact}>
                        <TextContainer>
                          <Text $variant={TextVariants.body.l} color={Colors.black}>
                            {contact}
                          </Text>
                        </TextContainer>
                        <div>
                          <Link color={Colors.blue[100]} href={`tel:${contact}`} target="_blank">
                            <IconWrapper data-testid="customer-phone-icon">
                              <Phone />
                            </IconWrapper>
                          </Link>
                        </div>
                      </CommonSection>
                    ),
                )}
            </>
          )}
          {type === 'projectManager' && (
            <ProjectManagerWrapper>
              <CustomerSection>
                <Text $variant={TextVariants.body.l} color={Colors.black}>
                  {item.label}
                </Text>
                <Text $variant={TextVariants.body.s} color={Colors.black}>
                  {item.subtitle}
                </Text>
              </CustomerSection>
              <CommonSection>
                <Text $variant={TextVariants.body.l} color={Colors.black}>
                  {item.value}
                </Text>
                <div>
                  <Link color={Colors.blue[100]} href={`${prefix}${item.value}`} target="_blank">
                    <IconWrapper data-testid="sidesheet-section-icon-manager">
                      <Mail />
                    </IconWrapper>
                  </Link>
                </div>
              </CommonSection>
            </ProjectManagerWrapper>
          )}
          {type !== 'customer' && type !== 'projectManager' && (
            <CommonSection>
              <TextContainer>
                <Text $variant={TextVariants.body.l} color={Colors.black}>
                  {item.label ?? item.value}
                </Text>
                {item.subtitle && (
                  <Text $variant={TextVariants.body.m} color={Colors.gray[100]}>
                    {item.subtitle}
                  </Text>
                )}
              </TextContainer>
              {isShowBtn && (
                <div>
                  <Link color={Colors.blue[100]} href={`${prefix}${item.value}`} target="_blank">
                    <IconWrapper data-testid="sidesheet-section-icon">{icon}</IconWrapper>
                  </Link>
                </div>
              )}
            </CommonSection>
          )}
        </div>
      ))}
    </Container>
  );
};
